import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL } from '../../utils';

const empty = {
  name: '',
  email: '',
  telephone: '',
  description: '',
  poste_id: '',
  store_id: '',
  statut: 'actif',
  date_embauche: '',
  date_sortie: '',
  type_contrat: '',
  date_naissance: '',
  situation_matrimoniale: '',
  nombre_enfants: 0,
  adresse: '',
  matricule: '',
  numero_cnss: '',
  numero_ifu: '',
  salaire_base: 0,
  indemnite_transport: 0,
  indemnite_logement: 0,
  taux_risque_professionnel: 1,
  indemnites: [],
};

const dateValue = (value) => (value ? String(value).slice(0, 10) : '');

const money = (value) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(Number(value) || 0))} F`;

const monthLabel = (period) => {
  const [year, month] = String(period || '').split('-');
  if (!year || !month) return period;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export default function DossierPersonnel() {
  const { id } = useParams();
  const [dossier, setDossier] = useState(null);
  const [form, setForm] = useState(empty);
  const [postes, setPostes] = useState([]);
  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const applyDossier = (data) => {
    setDossier(data);
    setForm({
      name: data.name || '',
      email: data.email || '',
      telephone: data.telephone || '',
      description: data.description || '',
      poste_id: data.poste_id || '',
      store_id: data.store_id || '',
      statut: data.statut || 'actif',
      date_embauche: dateValue(data.date_embauche),
      date_sortie: dateValue(data.date_sortie),
      type_contrat: data.type_contrat || '',
      date_naissance: dateValue(data.date_naissance),
      numero_cnss: data.numero_cnss || '',
      situation_matrimoniale: data.situation_matrimoniale || '',
      nombre_enfants: data.nombre_enfants ?? 0,
      adresse: data.adresse || '',
      matricule: data.matricule || '',
      numero_ifu: data.numero_ifu || '',
      salaire_base: data.salaire_base ?? 0,
      indemnite_transport: data.indemnite_transport ?? 0,
      indemnite_logement: data.indemnite_logement ?? 0,
      taux_risque_professionnel: data.taux_risque_professionnel || 1,
      indemnites: (data.indemnites || []).map((line) => ({
        label: line.label || '',
        amount: line.amount ?? 0,
      })),
    });
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dossierResponse, posteResponse, storeResponse] = await Promise.all([
        axios.get(`${APP_API_URL}/personnels/${id}/dossier`, { headers }),
        axios.get(`${APP_API_URL}/postes`, { headers }),
        axios.get(`${APP_API_URL}/stores`, { headers }),
      ]);
      applyDossier(dossierResponse.data.dossier);
      setPostes(posteResponse.data || []);
      setStores(storeResponse.data.data || storeResponse.data || []);
    } catch (err) {
      setError('Impossible de charger le dossier.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setIndemnite = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      indemnites: prev.indemnites.map((line, lineIndex) => (
        lineIndex === index ? { ...line, [key]: value } : line
      )),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${APP_API_URL}/personnels/${id}/dossier`, {
        ...form,
        poste_id: form.poste_id || null,
        store_id: form.store_id || null,
        indemnites: form.indemnites
          .map((line) => ({ label: String(line.label || '').trim(), amount: Number(line.amount) || 0 }))
          .filter((line) => line.label && line.amount > 0),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyDossier(response.data.dossier);
      setMessage('Dossier enregistré.');
    } catch (err) {
      const errors = err.response?.data?.errors;
      const first = errors ? Object.values(errors).flat()[0] : null;
      setError(first || err.response?.data?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const bulletins = dossier?.bulletins || [];

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Ressources humaines</p>
          <h1 className="duo-hero-title">{dossier?.name || 'Dossier personnel'}</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            {dossier?.poste || 'Fonction non renseignée'}
            {dossier?.store ? ` · ${dossier.store}` : ''}
          </p>
        </div>

        <div className="d-flex gap-2 mb-3 no-print">
          <Link to="/personnel" className="duo-btn duo-btn--ghost">Retour à la liste</Link>
          <Link to={`/personnel/paie/${id}?mois=${currentPeriod()}`} className="duo-btn duo-btn--primary">
            Bulletin du mois
          </Link>
        </div>

        {error && <p className="text-danger">{error}</p>}
        {message && <p className="text-success">{message}</p>}

        {isLoading ? (
          <p>Chargement du dossier…</p>
        ) : (
          <>
            <form onSubmit={save} className="duo-panel duo-panel--form mb-3">
              <div className="duo-form-stack">
                <h2 className="h6 mb-0">Compte et identité</h2>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Nom
                    <input type="text" className="form-control" value={form.name} onChange={(e) => setField('name', e.target.value)} />
                  </label>
                  <label>
                    Email
                    <input type="email" className="form-control" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                  </label>
                </div>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Téléphone
                    <input type="text" className="form-control" value={form.telephone} onChange={(e) => setField('telephone', e.target.value)} />
                  </label>
                  <label>
                    Statut
                    <select className="form-control" value={form.statut} onChange={(e) => setField('statut', e.target.value)}>
                      <option value="actif">Actif</option>
                      <option value="inactif">Inactif</option>
                    </select>
                  </label>
                </div>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Fonction
                    <select className="form-control" value={form.poste_id} onChange={(e) => setField('poste_id', e.target.value)}>
                      <option value="">—</option>
                      {postes.map((poste) => (
                        <option key={poste.id} value={poste.id}>{poste.titre}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Magasin
                    <select className="form-control" value={form.store_id} onChange={(e) => setField('store_id', e.target.value)}>
                      <option value="">—</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Date d&apos;embauche
                    <input type="date" className="form-control" value={form.date_embauche} onChange={(e) => setField('date_embauche', e.target.value)} />
                  </label>
                  <label>
                    Date de sortie
                    <input type="date" className="form-control" value={form.date_sortie} onChange={(e) => setField('date_sortie', e.target.value)} />
                  </label>
                </div>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Type de contrat
                    <select className="form-control" value={form.type_contrat} onChange={(e) => setField('type_contrat', e.target.value)}>
                      <option value="">—</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="essai">Période d&apos;essai</option>
                      <option value="stage">Stage</option>
                      <option value="journalier">Journalier</option>
                      <option value="prestation">Prestation</option>
                    </select>
                  </label>
                  <label>
                    Date de naissance
                    <input type="date" className="form-control" value={form.date_naissance} onChange={(e) => setField('date_naissance', e.target.value)} />
                  </label>
                </div>
                <p className="text-muted small mb-0">Une entrée ou une sortie en cours de mois réduit la paie de ce mois. Le mois complet compte 30 jours : une embauche le 20 donne 11 jours, du 20 au 30.</p>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Situation matrimoniale
                    <select className="form-control" value={form.situation_matrimoniale} onChange={(e) => setField('situation_matrimoniale', e.target.value)}>
                      <option value="">—</option>
                      <option value="celibataire">Célibataire</option>
                      <option value="marie">Marié(e)</option>
                      <option value="divorce">Divorcé(e)</option>
                      <option value="veuf">Veuf / veuve</option>
                    </select>
                  </label>
                  <label>
                    Nombre d&apos;enfants
                    <input type="number" min="0" max="20" className="form-control" value={form.nombre_enfants} onChange={(e) => setField('nombre_enfants', e.target.value)} />
                  </label>
                </div>
                <label>
                  Adresse
                  <input type="text" className="form-control" value={form.adresse} onChange={(e) => setField('adresse', e.target.value)} />
                </label>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Matricule
                    <input type="text" className="form-control" value={form.matricule} onChange={(e) => setField('matricule', e.target.value)} placeholder={dossier?.matricule_affiche || 'ASB-0000'} />
                  </label>
                  <label>
                    N° CNSS
                    <input type="text" className="form-control" value={form.numero_cnss} onChange={(e) => setField('numero_cnss', e.target.value)} />
                  </label>
                  <label>
                    N° IFU
                    <input type="text" className="form-control" value={form.numero_ifu} onChange={(e) => setField('numero_ifu', e.target.value)} />
                  </label>
                </div>
                <label>
                  Note
                  <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setField('description', e.target.value)} />
                </label>

                <h2 className="h6 mb-0 mt-2">Éléments de paie (mensuels, FCFA)</h2>
                <p className="duo-hero-sub mb-0">
                  Le SMIG est de 52 000 FCFA. La prime d&apos;ancienneté est calculée sur le salaire de base à partir de la date d&apos;embauche.
                  Les indemnités ajoutées ci-dessous entrent dans le brut, donc dans la CNSS, le VPS et l&apos;ITS.
                </p>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Salaire de base
                    <input type="number" min="0" step="1" className="form-control" value={form.salaire_base} onChange={(e) => setField('salaire_base', e.target.value)} />
                  </label>
                  <label>
                    Taux de risque professionnel CNSS
                    <select className="form-control" value={form.taux_risque_professionnel} onChange={(e) => setField('taux_risque_professionnel', e.target.value)}>
                      <option value="1">1 % — commerce</option>
                      <option value="2">2 %</option>
                      <option value="3">3 %</option>
                      <option value="4">4 %</option>
                    </select>
                  </label>
                </div>
                <div className="duo-form-row duo-form-row--2">
                  <label>
                    Indemnité de transport
                    <input type="number" min="0" step="1" className="form-control" value={form.indemnite_transport} onChange={(e) => setField('indemnite_transport', e.target.value)} />
                  </label>
                  <label>
                    Indemnité de logement
                    <input type="number" min="0" step="1" className="form-control" value={form.indemnite_logement} onChange={(e) => setField('indemnite_logement', e.target.value)} />
                  </label>
                </div>

                <h2 className="h6 mb-0">Autres indemnités et primes</h2>
                {form.indemnites.map((line, index) => (
                  <div key={index} className="d-flex gap-2 align-items-end">
                    <label className="flex-grow-1">
                      Libellé
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Prime de rendement, salissure…"
                        value={line.label}
                        onChange={(e) => setIndemnite(index, 'label', e.target.value)}
                      />
                    </label>
                    <label style={{ width: 160 }}>
                      Montant
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="form-control"
                        value={line.amount}
                        onChange={(e) => setIndemnite(index, 'amount', e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="duo-btn duo-btn--ghost"
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        indemnites: prev.indemnites.filter((_, lineIndex) => lineIndex !== index),
                      }))}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
                <div>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      indemnites: [...prev.indemnites, { label: '', amount: '' }],
                    }))}
                  >
                    Ajouter une indemnité
                  </button>
                </div>

                <div>
                  <button type="submit" className="duo-btn duo-btn--primary" disabled={saving}>
                    {saving ? 'Enregistrement…' : 'Enregistrer le dossier'}
                  </button>
                </div>
              </div>
            </form>

            <section className="duo-panel">
              <div className="duo-toolbar">
                <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
                  Fiches de paie
                </span>
                <Link to={`/personnel/paie/${id}?mois=${currentPeriod()}`} className="duo-btn duo-btn--ghost duo-btn--sm">
                  Préparer le mois en cours
                </Link>
              </div>
              <div className="duo-table-wrap">
                <table className="duo-table">
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th>Brut</th>
                      <th>Net</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {bulletins.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="duo-empty-row">Aucun bulletin enregistré pour ce compte.</td>
                      </tr>
                    ) : (
                      bulletins.map((slip) => (
                        <tr key={slip.id}>
                          <td className="text-capitalize">{monthLabel(slip.period)}</td>
                          <td>{money(slip.brut)}</td>
                          <td>{money(slip.net)}</td>
                          <td>
                            <Link className="duo-btn duo-btn--ghost duo-btn--sm" to={`/personnel/paie/${id}?mois=${slip.period}`}>
                              Voir
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </MainLayout>
  );
}
